import type {
  EventDayDto,
  EventDetailDto,
  EventListItemDto,
  EventOccurrenceDto,
} from '@/services/platform/api/model';
import { isRegistrationClosed } from './list/format';

/**
 * A campaign's days as the server lists them (`days[]`, from today up to 60 days ahead), or `null`
 * for an event without a recurrence — or from an API older than `days[]`. Salesforce materialises
 * no occurrence for a campaign's future runs, so `occurrences[]` holds only its long-past anchor:
 * judged by it, a running campaign would read as dated in the past and closed.
 */
export function campaignServerDays(event: Pick<EventDetailDto, 'recurrence' | 'days'>): EventDayDto[] | null {
  if (!event.recurrence?.frequency) return null;
  return event.days ?? null;
}

/** The occurrence behind a campaign day, if it has one (a derived day has none). */
function dayOccurrence(event: EventDetailDto, day: EventDayDto): EventOccurrenceDto | undefined {
  return day.occurrenceId
    ? event.occurrences?.find(occurrence => occurrence.id === day.occurrenceId)
    : undefined;
}

/** Contract v1.12 §3.2 — the only `bookingMode` value that disables the booking action. */
export const BOOKING_MODE_NONE = 'None';

/**
 * Whether the event shows a booking action. `allowsBooking` is server-computed (§3.2: absent
 * `bookingMode` means None) and is authoritative when present; the `bookingMode` check is a
 * defensive fallback only, never the primary source — trusting it alone would resurrect the
 * pre-v1.12 client-side derivation the backend deliberately removed.
 */
export function allowsEventBooking(
  event?: Pick<EventDetailDto, 'allowsBooking' | 'bookingMode'> | null
): boolean {
  if (event?.allowsBooking != null) return event.allowsBooking;
  return !!event?.bookingMode && event.bookingMode !== BOOKING_MODE_NONE;
}

/**
 * The gating fields relevant to booking, occurrence-first with the event's own fields as
 * fallback only (contract §3.4: validations are per occurrence; §3.1.1: the occurrence's venue
 * is authoritative — the event's top-level fields merely mirror occurrence #1).
 */
export interface EventBookingScope {
  guestLimit?: number | null;
  registrationDeadline?: string | null;
  availableSpots?: number | null;
  capacity?: number | null;
  status?: string | null;
  allDay?: boolean;
  isPaid?: boolean;
  price?: number | null;
  currencyCode?: string | null;
  isComplimentary?: boolean;
  venueName?: string | null;
  venueNodeKey?: string | null;
  bookingRef?: string | null;
}

export function resolveBookingScope(
  event?: EventDetailDto | null,
  occurrence?: EventOccurrenceDto | null
): EventBookingScope {
  // A campaign day derived from the recurrence has no occurrence, and the event's own deadline only
  // mirrors the long-past anchor — applied to a future run it would close every one of them.
  const isDerivedCampaignDay = !occurrence && !!event && !!campaignServerDays(event);
  return {
    guestLimit: occurrence?.guestLimit ?? event?.guestLimit,
    registrationDeadline: isDerivedCampaignDay
      ? undefined
      : (occurrence?.registrationDeadline ?? event?.registrationDeadline),
    availableSpots: occurrence?.availableSpots ?? event?.availableSpots,
    capacity: occurrence?.capacity ?? event?.capacity,
    status: occurrence?.status ?? event?.status,
    allDay: occurrence?.allDay ?? event?.allDay,
    isPaid: occurrence?.isPaid ?? event?.isPaid,
    price: occurrence?.price ?? event?.price,
    currencyCode: event?.currencyCode,
    isComplimentary: occurrence?.isComplimentary ?? event?.isComplimentary,
    venueName: occurrence?.venueName ?? event?.venueName,
    venueNodeKey: occurrence?.venueNodeKey ?? event?.venueNodeKey,
    bookingRef: occurrence?.bookingRef ?? event?.bookingRef,
  };
}

export type PaymentDecision = 'complimentary' | 'notForSale' | 'paid' | 'free';

/**
 * Contract §3.1.2 payment decision order: 1) complimentary → no payment; 2) paid with price
 * absent → do NOT sell; 3) paid with price → normal payment; 4) not paid → free registration.
 * `price == null` covers both an omitted key and an explicit null (the contract omits absent
 * fields rather than nulling them, but both must be treated the same).
 */
export function resolvePaymentDecision(scope: EventBookingScope): PaymentDecision {
  if (scope.isComplimentary) return 'complimentary';
  if (scope.isPaid && scope.price == null) return 'notForSale';
  if (scope.isPaid) return 'paid';
  return 'free';
}

/**
 * Contract §3.4: "capacity not defined = no limit" — with no capacity this is NEVER sold out,
 * regardless of `availableSpots`. Only meaningful with occurrence/detail-level data (`capacity`
 * exists there); the list card DTO has no `capacity` and uses {@link isCardSoldOut} instead — do
 * not route cards through this.
 */
export function isSoldOut(scope: Pick<EventBookingScope, 'availableSpots' | 'capacity' | 'status'>): boolean {
  if (scope.status === 'Full') return true;
  if (scope.capacity == null) return false;
  return scope.availableSpots != null && scope.availableSpots <= 0;
}

/** Contract §3.4: an occurrence not `Published` is rejected with 422. Absent status (pre-v1.12
 * events, or the event-level mirror) is assumed open — never invent a block from missing data. */
export function isOpenForRegistration(scope: Pick<EventBookingScope, 'status'>): boolean {
  return scope.status == null || scope.status === 'Published';
}

/**
 * Sold out as a list card (and the Which picker) can tell. The card is dated by the next date, which
 * may be full while a later one is still open — so when the API says whether ANY upcoming date can
 * be booked (`hasAvailableDates`), that alone decides, and an event with another open date stays
 * tappable. Without the field (an older API, or a detail DTO standing in for a card) it falls back
 * to the dated occurrence: `status` `Full` — set by hand in any booking mode, possibly with seats
 * still counted — or no seats left. Deliberately `=== 0`, never `<= 0`/null: a null count means no
 * seat count at all (every non-Salesforce event), which must never read as sold out.
 */
export function isCardSoldOut(
  event?: Pick<EventListItemDto, 'availableSpots' | 'status' | 'hasAvailableDates'> | null
): boolean {
  if (event?.hasAvailableDates != null) return !event.hasAvailableDates;
  return event?.status === 'Full' || event?.availableSpots === 0;
}

/**
 * The "N seats left" a card may show — those of the date it is dated by. `undefined` when the card
 * is sold out, when that date is `Full` (its count no longer means anything bookable, even if a
 * later date keeps the card tappable), or when there is no positive count to show.
 */
export function cardSeatsLeft(
  event?: Pick<EventListItemDto, 'availableSpots' | 'status' | 'hasAvailableDates'> | null
): number | undefined {
  if (!event || isCardSoldOut(event) || event.status === 'Full') return undefined;
  return typeof event.availableSpots === 'number' && event.availableSpots > 0
    ? event.availableSpots
    : undefined;
}

/**
 * The largest party one registration may hold, member included. `guestLimit` counts companions
 * only (contract §3.4): null = no limit → `undefined`, never a fabricated cap; 0 = the member
 * alone → 1; N companions → N + 1.
 */
export function resolveGuestLimit(scope: Pick<EventBookingScope, 'guestLimit'>): number | undefined {
  if (scope.guestLimit == null || scope.guestLimit < 0) return undefined;
  return scope.guestLimit + 1;
}

/**
 * EDP aggregate for a multi-occurrence event — the event-level mirror (occurrence #1) must not
 * gate the whole event: a sold-out first Saturday should not disable a still-open Sunday.
 */
export function isEveryOccurrenceSoldOut(event: EventDetailDto): boolean {
  // A campaign is judged by its days to come; a derived day takes the event's own fields.
  const days = campaignServerDays(event);
  if (days) {
    return (
      days.length > 0 && days.every(day => isSoldOut(resolveBookingScope(event, dayOccurrence(event, day))))
    );
  }
  const occurrences = event.occurrences ?? [];
  if (occurrences.length === 0) return isSoldOut(resolveBookingScope(event));
  return occurrences.every(occurrence => isSoldOut(resolveBookingScope(event, occurrence)));
}

/**
 * EDP aggregate mirroring `isEveryOccurrenceSoldOut` for the registration deadline. A campaign is
 * closed once it has no day left; a derived day has no deadline of its own (the anchor's has long
 * passed), so only a day backed by an occurrence can close on one.
 */
export function isEveryOccurrenceClosed(event: EventDetailDto): boolean {
  const days = campaignServerDays(event);
  if (days) {
    return days.every(day => {
      const occurrence = dayOccurrence(event, day);
      return (
        !!occurrence && isRegistrationClosed(occurrence.registrationDeadline ?? event.registrationDeadline)
      );
    });
  }
  const occurrences = event.occurrences ?? [];
  if (occurrences.length === 0) return isRegistrationClosed(event.registrationDeadline);
  return occurrences.every(occurrence =>
    isRegistrationClosed(occurrence.registrationDeadline ?? event.registrationDeadline)
  );
}

export type RegistrationErrorReason =
  'soldOut' | 'dateGone' | 'closed' | 'invalid' | 'paymentRequired' | 'unknown';

/**
 * `registerEventV1`'s error responses mostly carry no distinguishing body (contract §3.3) —
 * the HTTP status is the primary signal. `409` is only ever "not enough seats" (re-registering on
 * the same date updates in place, so there is no "already registered"; the body's
 * `availableSpots` is read separately — see `EventRegistration`). `404` means the chosen date no
 * longer exists in Salesforce — the FE drops it and re-reads the event. `422` not
 * published/deadline expired/wrong channel/paid-without-price; `400` a request the FE shouldn't
 * have been able to send (guest count, missing occurrence). One `400` case does carry a readable
 * `ProblemDetails.detail` (a paid event registered without a payment reference) — that one is
 * singled out via `detail` before falling back to the generic `invalid` bucket.
 */
export function resolveRegistrationErrorReason(status?: number, detail?: string): RegistrationErrorReason {
  if (status === 400 && detail?.startsWith('VALIDATION_ERROR: Paid event requires payment reference')) {
    return 'paymentRequired';
  }
  if (status === 409) return 'soldOut';
  if (status === 404) return 'dateGone';
  if (status === 422) return 'closed';
  if (status === 400) return 'invalid';
  return 'unknown';
}
